import React, { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import {
  Box,
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  AddCircle,
  BarChartOutlined,
  Construction,
  Delete,
  Edit,
  Output,
  Share,
} from "@mui/icons-material";
import Grid from "@mui/material/Grid2";

import { AudienceSkeleton } from "./AudienceSkeleton";
import { PageContainer } from "@/components/containers";
import {
  ActionButtons,
  AudienceSize,
  BuildModeSelect,
  HelpBox,
  HelpText,
  LocationSelection,
  QueryGroup,
  QueryGroupOperator,
} from "@/components/atoms";
import { AudienceMiniStats } from "@/components/molecules";
import { useAudienceContext } from "@/context";
import { useAudience, useAudienceUtils, useActionWorkflow, useLocations, useServices, useVariables } from "@/hooks";
import { GeoFenceLocation } from "@/types";
import {
  checkVariables,
  createBuildDefinition,
} from "@/utils";

const AudiencePage: React.FC = () => {

  const { ui } = useServices();
  const { state, dispatch } = useAudienceContext();

  // Fetch Page Data
  let { audienceId } = useParams<{ audienceId: string }>();
  audienceId = audienceId === "new" ? undefined : audienceId;
  const {
    audience, loading: audLoad, error: audErr,
  } = useAudience(audienceId, "build");
  const {
    variables, loading: varLoad, error: varErr,
  } = useVariables();
  const {
    locations, loading: locLoad, error: locErr,
  } = useLocations();

  const { resetAction } = useActionWorkflow();
  const { utils, action } = useAudienceUtils(audienceId, audience);

  // Derive a single Page State
  const pageState = useMemo(() => {

    // Fetch status
    const isLoading = audLoad || varLoad || locLoad;
    const loadError = !!(audErr || varErr || locErr);

    // Variable status
    const isVarInvalid = (audience && variables)
      && !checkVariables(audience, variables);
    const hasVars = audience?.var_groups[0].variables.length > 0

    // Audience status
    const isNewAudience = audienceId === undefined;
    const isAudShared = !!audience?.shared;
    const isAudProcessing = audience?.status === 'processing';
    const isAudComplete = audience?.status === 'complete';
    const isAudError = audience?.status === 'error';

    // Build status
    const isBuilt = !!(audience?.audience_size && audience?.audience_size.length > 0);
    const justRebuilt = action.type === 'build' && action.status === 'success';
    const audDefinition = createBuildDefinition(audience);
    const hasChanged = audDefinition !== state.currentDefinition;

    // Insight status
    const insightIsStale = hasChanged || justRebuilt;

    // Action status
    const isActioning = action.status === 'loading';

    // Locked status
    const isLocked = isLoading || loadError || isAudProcessing || isAudShared || isActioning;

    return {
      isLoading,  // Data is loading
      isReady: !isLoading && !loadError, // Data is ready to go
      isLocked,  // Edit access is disabled
      isActioning, // Page is doing something
      isBuilt,  // The audience has been built
      hasChanged, // Has the audience definition changed?
      insightIsStale, // Does the insight need regenerating?

      errors: {
        // Page Errors
        AudienceNotFound: audErr,
        AudienceError: isAudError,
        DataUnavailable: varErr || locErr,
        InvalidVariables: isVarInvalid,

        // Action Errors
        BuildError: (action.type === 'build' && action.status === 'error' && !hasChanged) && action.error,
        InsightError: (action.type === 'insight' && action.status === 'error' && !hasChanged) && action.error,

        // Audience Status
        AudienceProcessing: isAudProcessing,
        AudienceShared: isAudShared,
      },

      can: {
        build: hasChanged && hasVars && !isLocked && !isVarInvalid && !isActioning,
        insight: isBuilt && !hasChanged && !(isLoading || loadError || isAudProcessing || isActioning),
        export: isBuilt && !hasChanged && !(isLoading || loadError || isAudProcessing || isActioning) && isAudComplete,
        delete: isBuilt && !hasChanged && audience && !isNewAudience && !isAudShared && !isLocked && !isActioning,
        share: isBuilt && !hasChanged && audience && !isNewAudience && !isAudShared && !isLocked && !isActioning,
      }

    };
  }, [state, action, audience, audienceId, variables, audLoad, varLoad, locLoad, audErr, varErr, locErr]);

  console.log(pageState.hasChanged, pageState.insightIsStale);
  console.log(action.type, action.status);

  useEffect(() => {
    if (pageState.hasChanged) {
      resetAction();
    }
  }, [pageState.hasChanged, resetAction]);

  const numQueryGroups = audience?.var_groups?.length || 0;

  const handleAddGroup = () => {
    dispatch({ type: "ADD_VARIABLE_GROUP" });
  };

  const handleChangeOperator = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: "CHANGE_OPERATOR", payload: event.target.value });
  };

  const handleChangeMode = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: "CHANGE_BUILD_MODE", payload: event.target.value });
  };

  const handleSelectLocation = (locations: GeoFenceLocation[]) => {
    dispatch({ type: "SET_GEO_FENCE", payload: locations });
  };

  const pageActions = useMemo(() => {
    const isBuilt = pageState.isBuilt;
    const hasChanged = pageState.hasChanged;
    const isComplete = audience?.status === 'complete';
    const isLoading = (type: string) => action.status === 'loading' && action.type === type;

    return [
      {
        id: 'delete',
        label: 'Delete',
        icon: Delete,
        onClick: () =>
          ui.openDialog({
            type: "deleteAudience",
            props: {
              audience,
              redirect: "/audience",
            },
          }),
        disabled: !pageState.can.delete,
        isPrimary: false,
        color: 'error' as const
      },
      {
        id: 'share',
        label: 'Share',
        icon: Share,
        onClick: () =>
          ui.openDialog({
            type: "shareAudience",
            props: {
              selectedEntity: audience,
              onShare: async (ids: number[]) => {
                await utils.handleShare(ids);
              },
            },
          }),
        disabled: !pageState.can.share,
        isPrimary: false,
      },
      {
        id: 'build',
        label: isBuilt ? 'Rebuild' : 'Build',
        icon: Construction,
        onClick: utils.handleBuild,
        loading: isLoading('build'),
        disabled: !pageState.can.build,
        isPrimary: (pageState.can.build || isLoading('build')) && (!isBuilt || hasChanged),
      },
      {
        id: 'insight',
        label: 'Insight',
        icon: BarChartOutlined,
        onClick: () => utils.handleInsight(pageState.insightIsStale),
        loading: isLoading('insight'),
        disabled: !pageState.can.insight,
        isPrimary: isBuilt && !hasChanged,
      },
      {
        id: 'export',
        label: 'Export',
        icon: Output,
        onClick: utils.handleExport,
        disabled: !pageState.can.export,
        isPrimary: isBuilt && !hasChanged && isComplete,
      }
    ]
  }, [action, audience, pageState, ui, utils])

  return (
    <PageContainer
      actions={<ActionButtons actions={pageActions} />}
      errors={pageState.errors}
      loading={action.status === 'loading'}
      loadingText={action.message}
    >
      {pageState.isLoading ? (
        <AudienceSkeleton />
      ) : (
        <Grid container spacing={2} sx={{ pb: 4 }}>
          {audience && variables && locations && (
            <>
              {/* Audience Name and Summary */}
              <Grid
                size={12}
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="h6">
                    {audience.audience_name || "New Audience"}
                  </Typography>
                  {audience.project_name && (
                    <Chip size="small" label={audience.project_name} />
                  )}
                  {audienceId && !pageState.isLocked && (
                    <HelpBox title="Edit Audience/Project Name">
                      <IconButton
                        size="small"
                        onClick={() =>
                          ui.openDialog({
                            type: "editAudience",
                            props: {
                              audienceId: audienceId,
                              audienceName: audience.audience_name,
                              projectName: audience.project_name,
                              onConfirm: async (
                                audienceId: string,
                                audienceName: string,
                                projectName: string | null
                              ) => {
                                await utils.handleUpdate(
                                  audienceId,
                                  audienceName,
                                  projectName
                                );
                              },
                            },
                          })
                        }
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </HelpBox>
                  )}
                </Stack>
                {audience.audience_size && (
                  <AudienceMiniStats audienceSize={audience.audience_size[0]} />
                )}
              </Grid>
              <Grid size={12}>
                <HelpText
                  text="Audience Query Groups"
                  helpText="
                  A query group is a set of variables that are combined using a boolean operator (e.g. AND/OR) to define an
                  audience behaviour
                  "
                  variant="body1"
                />
              </Grid>
              {audience.var_groups.map((group, index) => (
                <React.Fragment key={index}>
                  {/*TODO - Reset needs to revert back to previous state not clear - or call it clear*/}
                  <QueryGroup
                    id={group.id}
                    variables={variables}
                    disabled={pageState.isLocked}
                  />
                  {numQueryGroups > 1 && index + 1 !== numQueryGroups && (
                    <Grid size={12}>
                      <QueryGroupOperator
                        value={audience.operator}
                        onChange={handleChangeOperator}
                        disabled={pageState.isLocked}
                      />
                    </Grid>
                  )}
                </React.Fragment>
              ))}
              {!pageState.isLocked && (
                <Grid size={12}>
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    border={1}
                    borderRadius={1}
                    borderColor={ui.theme.palette.divider}
                    p={1}
                  >
                    <HelpBox title="Add Query Group">
                      <IconButton size="small" onClick={handleAddGroup}>
                        <AddCircle color="secondary" />
                      </IconButton>
                    </HelpBox>
                  </Box>
                </Grid>
              )}
              <Grid size={12} mt={2}>
                <HelpText
                  text="Audience Settings"
                  helpText="Additional settings to configure your audience further"
                  variant="body1"
                />
              </Grid>
              <Grid>
                <BuildModeSelect
                  value={audience.build_mode}
                  onChange={handleChangeMode}
                  disabled={pageState.isLocked}
                />
              </Grid>
              <Grid>
                <LocationSelection
                  locations={locations}
                  selection={audience.geo_fence}
                  onSelect={handleSelectLocation}
                  disabled={pageState.isLocked}
                />
              </Grid>
              {audience.audience_size && (
                <>
                  <Grid size={12} mt={2}>
                    <HelpText
                      text="Audience Size"
                      helpText="Understand the reach of your audience"
                      variant="body1"
                    />
                  </Grid>
                  <AudienceSize data={audience.audience_size[0]} />
                </>
              )}
            </>
          )}
        </Grid>
      )}
    </PageContainer>
  );
};

export default AudiencePage;
